/*
 * Purpose: Dashboard Learning flows — guided template curriculum
 * Last modified: 2026-07-01 | Agent
 * Completeness: 100
 */
import { Link } from 'react-router-dom';
import { BookOpen, CheckCircle2, Copy, ArrowRight } from 'lucide-react';
import { Card } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import {
  buildClonedFlowTemplate,
  flowTemplateDisplayName,
  flowTemplatesLearningHref,
  getLearningFlows,
} from '@/lib/flowTemplates';
import { useWorkflowStore } from '@/stores/workflowStore';
import { toast } from 'sonner';
import { cn } from '@/lib/utils';

function difficultyClass(difficulty: string) {
  switch (difficulty) {
    case 'Beginner':
      return 'border-green-500/30 text-green-700 dark:text-green-400';
    case 'Intermediate':
      return 'border-blue-500/30 text-blue-700 dark:text-blue-400';
    case 'Advanced':
      return 'border-purple-500/30 text-purple-700 dark:text-purple-400';
    case 'Expert':
      return 'border-red-500/30 text-red-700 dark:text-red-400';
    default:
      return '';
  }
}

export function LearningFlowsPanel({ className }: { className?: string }) {
  const importWorkflow = useWorkflowStore((s) => s.importWorkflow);
  const steps = getLearningFlows();

  const handleClone = (step: (typeof steps)[number]) => {
    importWorkflow(buildClonedFlowTemplate(step.template));
    toast.success(`"${flowTemplateDisplayName(step.template.name)}" cloned — open Map to practice`, {
      action: {
        label: 'Open flows',
        onClick: () => {
          window.location.href = '/flows';
        },
      },
    });
  };

  return (
    <section className={cn('space-y-4', className)} aria-labelledby="learning-flows-heading">
      <div className="flex flex-col sm:flex-row sm:items-end sm:justify-between gap-3">
        <div>
          <h2
            id="learning-flows-heading"
            className="text-lg font-semibold text-foreground flex items-center gap-2"
          >
            <BookOpen className="h-5 w-5 text-[hsl(var(--ab-green-deep))] dark:text-[hsl(var(--ab-green-glow))]" />
            Learning flows
          </h2>
          <p className="text-xs text-muted-foreground mt-1 max-w-xl">
            Clone each starter in order — learn triggers, validation, APIs, and pipelines on the Mission map.
          </p>
        </div>
        <Button asChild size="sm" actionTier="ghost" className="shrink-0">
          <Link to={flowTemplatesLearningHref()}>
            Full learning path
            <ArrowRight className="h-3.5 w-3.5 ml-1" />
          </Link>
        </Button>
      </div>

      <div className="grid gap-3 lg:grid-cols-2">
        {steps.map((step) => (
          <Card
            key={step.templateKey}
            className="p-4 sm:p-5 border-border/80 hover:border-primary/25 transition-colors"
          >
            <div className="flex gap-3 sm:gap-4">
              <div
                className="flex h-9 w-9 shrink-0 items-center justify-center rounded-full bg-primary/15 text-sm font-bold text-primary"
                aria-hidden
              >
                {step.order}
              </div>
              <div className="min-w-0 flex-1 space-y-2">
                <div className="flex flex-wrap items-start gap-2">
                  <h3 className="font-semibold text-sm text-foreground leading-snug">
                    {flowTemplateDisplayName(step.template.name)}
                  </h3>
                  <Badge variant="outline" className={cn('text-[10px]', difficultyClass(step.template.difficulty))}>
                    {step.template.difficulty}
                  </Badge>
                  <span className="text-[10px] text-muted-foreground">~{step.estMinutes} min</span>
                </div>
                <p className="text-xs text-muted-foreground">{step.lesson}</p>
                <ul className="space-y-1">
                  {(step.template.features || []).slice(0, 2).map((feature) => (
                    <li key={feature} className="flex items-start gap-1.5 text-[11px] text-muted-foreground">
                      <CheckCircle2 className="h-3 w-3 shrink-0 mt-0.5 text-[hsl(var(--ab-green-core))]" />
                      {feature}
                    </li>
                  ))}
                </ul>
                <Button size="sm" actionTier="secondary" className="h-8 text-xs mt-1" onClick={() => handleClone(step)}>
                  <Copy className="h-3.5 w-3.5 mr-1" />
                  {step.ctaLabel}
                </Button>
              </div>
            </div>
          </Card>
        ))}
      </div>
    </section>
  );
}

export default LearningFlowsPanel;
